<script>
    import { apiMethods } from "../../Services/index.svelte";
    import { isObjectEmpty, showError } from "../../Utils/utilFunctions.svelte";
    import { NotificationModal, PageLoading } from "./index.svelte";

    export let isModalOpen = false;
    export let encounterId, accountId;

    $: if (isModalOpen) getOcrStickers();
    else {
        latestOcrSticker = {};
        isImgExpanded = false;
        isPreviewImgExpanded = false;
    }

    $: if (isUploadStickerModalOpen)
        setTimeout(() => enableFileDragAndDrop(), 0);
    else disableFileDragAndDrop();

    let isLoadingOcrStickers = false,
        latestOcrSticker = {};

    let isUploadStickerModalOpen = false,
        isUploadingSticker = false,
        selectedSticker,
        stickerFileInput;

    let isImgExpanded = false,
        isPreviewImgExpanded = false;

    const stickerAcceptedTypes = ["image/png", "image/jpeg", "image/jpg"];

    const getOcrStickers = async () => {
        if (!encounterId || isLoadingOcrStickers) return;

        isLoadingOcrStickers = true;

        await apiMethods.getMethod
            .getOcrStickers(0, 999999, {
                "filter[patientEncounterId]": encounterId,
            })
            .then((res) => {
                if (!res || res.error) {
                    showError(
                        res.error ??
                            "Failed to load encounter OCR stickers, please refresh the page and try again and contact support if the issue persists.",
                    );
                    isModalOpen = false;
                } else latestOcrSticker = res.results[0] ?? {};
            })
            .catch((err) => {
                showError(err);
                isModalOpen = false;
            });

        isLoadingOcrStickers = false;
    };

    const onFileChange = (e, isDropFile = false) => {
        const file = isDropFile ? e.dataTransfer.files[0] : e.target.files[0];

        const fileType = file?.type;

        if (!fileType || !stickerAcceptedTypes.includes(fileType)) return;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
            selectedSticker = {
                type: fileType,
                content: e.target.result,
            };
        };

        isPreviewImgExpanded = false;
    };

    const uploadSticker = async () => {
        isUploadingSticker = true;

        await apiMethods.postMethod
            .createOcrSticker({
                accountId,
                inputPatientEncounterId: encounterId,
                inputBase64: selectedSticker.content.split(",")[1],
            })
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to upload OCR sticker, please refresh the page and try again and contact support if the issue persists.",
                    );
                else await getOcrStickers();
            })
            .catch((err) => showError(err));

        isUploadingSticker = false;

        closeUploadStickerModal();

        isImgExpanded = false;
        isPreviewImgExpanded = false;
    };

    const enableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");
        const uploadStickerModal =
            document.getElementById("uploadStickerModal");

        uploadStickerModal.addEventListener("dragover", (e) => {
            e.preventDefault();

            enableDropArea.classList.add("outline-dashed");
            enableDropArea.classList.add("outline-secondary");
            enableDropArea.classList.add("outline-2");
        });

        uploadStickerModal.addEventListener("dragleave", (e) => {
            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        uploadStickerModal.addEventListener("drop", (e) => {
            e.preventDefault();

            enableDropArea.classList.remove("outline-dashed");
            enableDropArea.classList.remove("outline-secondary");
            enableDropArea.classList.remove("outline-2");
        });

        enableDropArea.addEventListener("drop", function (e) {
            e.preventDefault();

            onFileChange(e, true);

            this.classList.remove("outline-dashed");
            this.classList.remove("outline-secondary");
            this.classList.remove("outline-2");
        });
    };

    const disableFileDragAndDrop = () => {
        const enableDropArea = document.getElementById("enableDropArea");

        enableDropArea &&
            enableDropArea.removeEventListener("drop", onFileChange);
    };

    const openUploadStickerModal = () => (isUploadStickerModalOpen = true);

    const closeUploadStickerModal = () => {
        isUploadStickerModalOpen = false;
        selectedSticker = undefined;
    };
</script>

<NotificationModal
    {isModalOpen}
    title="Encounter OCR Stickers"
    showConfirmBtn={false}
    cancelText="Close"
    closeModalFunction={() => (isModalOpen = false)}
    closeModal={() => (isModalOpen = false)}
    modalMaxWidth={`${isImgExpanded ? "max-w-fit" : ""}`}
    headerOptions={[
        {
            isHidden: isObjectEmpty(latestOcrSticker),
            iFont: "fa-solid fa-image",
            iconName: "createOcrStickerIcon",
            optionText: "Upload",
            function: () => openUploadStickerModal(),
        },
    ]}
>
    <div class="flex flex-col flex-1 gap-y-4 overflow-y-auto">
        {#if isLoadingOcrStickers}
            <p>Loading OCR stickers...</p>
            <PageLoading />
        {:else if isObjectEmpty(latestOcrSticker)}
            <div class="flex flex-1 items-center gap-y-4 flex-col">
                <p>No stickers for the encounter</p>
                <button
                    class="btn btn-md btn-secondary"
                    on:click|preventDefault={openUploadStickerModal}
                >
                    Upload
                </button>
            </div>
        {:else}
            <div
                class={`flex items-center justify-center ${isImgExpanded ? "cursor-zoom-out" : "avatar cursor-zoom-in"}`}
            >
                <div class={`rounded-xl ${isImgExpanded ? "" : "w-48"}`}>
                    <img
                        src={`data:image/jpeg;base64,${latestOcrSticker.jpgContent}`}
                        alt=""
                        on:click|preventDefault={() =>
                            (isImgExpanded = !isImgExpanded)}
                    />
                </div>
            </div>
        {/if}
    </div>
</NotificationModal>

<NotificationModal
    isModalOpen={isUploadStickerModalOpen}
    title="Upload Sticker"
    cancelText="Cancel"
    closeModal={closeUploadStickerModal}
    submitText="Upload"
    closeModalFunction={closeUploadStickerModal}
    confirmAction={uploadSticker}
    isConfirming={isUploadingSticker}
    customizeId="uploadStickerModal"
    modalMaxWidth={`${isPreviewImgExpanded ? "max-w-fit" : ""}`}
>
    <div class="flex flex-col gap-y-4 flex-1">
        <div
            class="flex items-center justify-center gap-x-4 w-full my-4"
            id="enableDropArea"
        >
            <button
                class="btn btn-sm btn-secondary"
                on:click|preventDefault={() => stickerFileInput.click()}
            >
                SELECT OR DRAG THE STICKER IMAGE HERE
            </button>

            <input
                type="file"
                accept=".jpg, .jpeg, .png"
                style="display: none"
                on:change={onFileChange}
                bind:this={stickerFileInput}
            />
        </div>

        {#if selectedSticker && selectedSticker.content}
            <div
                class={`flex items-center justify-center ${isPreviewImgExpanded ? "overflow-y-auto h-full cursor-zoom-out" : "avatar cursor-zoom-in"}`}
            >
                <div
                    class={`rounded-xl ${isPreviewImgExpanded ? "max-h-[500px]" : "w-48"}`}
                >
                    <img
                        src={selectedSticker.content}
                        alt=""
                        on:click|preventDefault={() =>
                            (isPreviewImgExpanded = !isPreviewImgExpanded)}
                    />
                </div>
            </div>
        {/if}
    </div>
</NotificationModal>
